import { IClient, ICoupon } from "@interfaces";
import React, { memo, useEffect, useMemo, useState } from "react";
import * as Yup from "yup";
import { Input, Form, Row, Col, FormInstance, DatePicker, Select, Spin, AutoComplete } from "antd";
import { useYup } from "@validations";
import { useTranslation } from "react-i18next";

import { FormAction } from "@components";
import { PATH_COUPON_LIST, dateFormat, serviceCategory } from "@configs";
import { selectApp, setLoading } from "@redux";
import {
  getApiErrorMessage,
  useGenerateCoupon,
  useLookupClientByPhone,
  useNotify,
  useSearchCouponFamilies,
} from "@utils";
import { useDispatch, useSelector } from "react-redux";
import { useHistory } from "react-router";
import dayjs, { Dayjs } from "dayjs";
import { SharedButton, SharedIconButton } from "@components";
import { SearchOutlined } from "@ant-design/icons";
import { ComponentClient } from "./ClientCard";
import styled from "styled-components";
const { RangePicker } = DatePicker;
const style = { background: "#00b96b" };
interface IProps {
  coupon?: ICoupon;
}
const dateStyle = { width: "100%", height: 48 };
const rangePresets: {
  label: string;
  value: [Dayjs, Dayjs];
}[] = [
  { label: "Last 7 Days", value: [dayjs().add(-7, "d"), dayjs()] },
  { label: "Last 14 Days", value: [dayjs().add(-14, "d"), dayjs()] },
  { label: "Last 30 Days", value: [dayjs().add(-30, "d"), dayjs()] },
  { label: "Last 90 Days", value: [dayjs().add(-90, "d"), dayjs()] },
];

const initValue: Partial<ICoupon> = {
  programName: "",
  serviceName: "",
  couponFamilyId: "",
  clinicId: "",
  validFrom: "",
  date: [],
  serviceCategory:""
};
export const GenerateCouponForm = memo(({ coupon }: IProps) => {
  const formRef = React.useRef<FormInstance>(null);
  const { YupGenerateCoupon } = useYup();
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const history = useHistory();
  const { success, error } = useNotify();
  const generateCoupon = useGenerateCoupon();
  const lookupClient = useLookupClientByPhone();
  const generateCouponSchema = Yup.object().shape(YupGenerateCoupon);
  const [genereateForm] = Form.useForm();
  const [client, setClient] = useState<IClient | null>(null);
  const { loading } = useSelector(selectApp);
  const [familyId, setFamilyId] = useState<string>("");

  const phoneNum: string = Form.useWatch("userId", genereateForm);
  const couponFamilyId: string = Form.useWatch("couponFamilyId", genereateForm);
  const { data: familyResults = [] } = useSearchCouponFamilies(couponFamilyId);

  useEffect(() => {
    dispatch(setLoading(generateCoupon.isPending || lookupClient.isPending));
  }, [dispatch, generateCoupon.isPending, lookupClient.isPending]);

  const family = useMemo(() => {
    return (familyResults as ICoupon[]).reduce((acc, item) => {
      acc.push({ ...item, value: item?.familyName || "", label: item?.familyName || "" });
      return acc;
    }, [] as ICoupon[]);
  }, [familyResults]);

  const onFinish = async (values: ICoupon) => {
    const { date } = values;
    const from = date ? date[0] : "";
    const to = date ? date[1] : "";
    try {
      await generateCoupon.mutateAsync({
        ...values,
        validFrom: dayjs(from).format(),
        validTo: dayjs(to).format(),
        userId: client ? client?.userId : "",
        couponFamilyId: familyId ? familyId : values?.familyName,
      } as ICoupon);
      success(t("message.create.success"));
      history.push(PATH_COUPON_LIST);
    } catch (err) {
      error(getApiErrorMessage(err, t("message.create.fail")));
    }
  };

  const yupSync = {
    async validator({ field }: any, value: any) {
      await generateCouponSchema.validateSyncAt(field, { [field]: value });
    },
  };
  const handleSearch = async () => {
    try {
      const res = await lookupClient.mutateAsync(phoneNum);
      if (res.data) {
        setClient(res.data);
        success(t("page.found_a_patient"));
      } else {
        setClient(null);
        error(t("message.not_found_patient"));
      }
    } catch (err) {
      error(getApiErrorMessage(err, t("message.not_found_patient")));
    }
  };

  const handleSearchFamily = (value: string) => {
    genereateForm.setFieldsValue({ couponFamilyId: value });
    setFamilyId("");
  };
  const onSelectProgram = (value: string, option: ICoupon) => {
    setFamilyId(option?._id || "");
  };
  const goBack = () => history.goBack();
  return (
    <Form
      form={genereateForm}
      name="basic"
      ref={formRef}
      initialValues={initValue}
      onFinish={onFinish}
      layout="vertical"
    >
      <Row gutter={{ md: 54, lg: 54 }}>
        <Col lg={12} md={24} sm={24} xs={24}>
          <Form.Item label={t("page.coupon_family_name")} name="couponFamilyId" rules={[yupSync]}>
            <AutoComplete
              options={family}
              onSelect={onSelectProgram}
              onSearch={handleSearchFamily}
              placeholder="type program name"
            />
          </Form.Item>
        </Col>
        <Col lg={12} md={24} sm={24}>
          <Form.Item label={t("page.service_category")} name="serviceCategory" rules={[yupSync]}>
            <Select>            
              {serviceCategory.map((item) => (
                <Select.Option value={item} key={item}>
                  {item}
                </Select.Option>
              ))}
            </Select>
          </Form.Item>
        </Col>
      </Row>
      <Row gutter={{ md: 54, lg: 54 }}>
        <Col lg={12} md={24} sm={24} xs={24}>
          <Form.Item label={t("page.valid_from_to")} name="date" rules={[yupSync]}>
            <RangePicker
              format={dateFormat}
              style={dateStyle}
              presets={rangePresets}
              size="large"
            />
          </Form.Item>
        </Col>
        <Col lg={12} md={24} sm={24} xs={24}>
          <Form.Item name={"userId"} label={t("page.dedicated_patient_number")} rules={[yupSync]}>
            <Row gutter={20}>
              <Col span={20}>
                <Input placeholder={t("page.placeholder_phone")} />
              </Col>
              <Col span={4}>
                <SharedIconButton onClick={handleSearch} icon={<SearchOutlined />} style={style} />
              </Col>
            </Row>
          </Form.Item>
        </Col>
      </Row>
      <Form.Item>
        <StyledFormAction>
          <SharedButton type="default" htmlType="button" size="middle" onClick={goBack}>
            {t("page.cancel")}
          </SharedButton>
          <SharedButton type="primary" htmlType="submit" size="middle" disabled={!client}>
            {t("page.create")}
          </SharedButton>
        </StyledFormAction>
      </Form.Item>
      {client && <ComponentClient loading={loading} client={client} />}
    </Form>
  );
});
const StyledFormAction = styled.div`
  display: flex;
  justify-content: flex-end;
  column-gap: 40px;
`;
